/**
 * 公開プロフィールの slug（`soremo.jp/{slug}`）が、既存のアプリルートや
 * プロジェクトルート、CloudFront で旧 Django 環境へルーティングされるパスと
 * 衝突しないためのガード。
 *
 * ルート直下の 1 セグメントは RootSegmentDispatcher が UUID（プロジェクト）／
 * slug（公開プロフィール）を振り分けるが、ここに列挙したトップレベルパスは
 * 公開プロフィールとして解釈しない。BE 側 slug バリデーション
 * （accounts/serializers.py の RESERVED_VANITY_SLUGS）と同一リストを維持すること。
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  // SPA トップレベルルート（App.tsx / routes.tsx）
  "invite",
  "signin",
  "signup",
  "password-reset",
  "projects",
  "preview",
  "tools",
  "os",
  "careers",
  "contact",
  "free-sound-assets",
  "my-page",
  "my-profile",
  "gallery",
  "roles",
  "payment",
  "project-info",
  "project-config",
  "project-creation",
  "talkroom",
  "dm",
  "home",
  "notes",
  "taskboard",
  "account",
  "console",
  "profile",
  "share",
  // ビルド成果物 / 静的アセットのディレクトリ
  "assets",
  "ffmpeg",
  "images",
  "favicon",
  // BE / インフラ予約（CloudFront で旧 Django 環境へルーティングされるパス）
  "spa",
  "api",
  "ws",
  "mcp",
  "admin",
  "accounts",
  "oauth",
  "ping",
  "landingpage",
  "payments",
  "mileages",
  "common",
  "jsi18n",
  "aoiumito",
  "privacypolicy",
  "termsofservice",
  "tokushoho",
  "about",
  "updateinfo",
  "mgk",
  "static",
  "media",
  "index.html",
  "favicon.ico",
  "robots.txt",
  "sitemap.xml",
]);

/** vanity_slug として許容する文字（半角英数 + ハイフン + アンダースコア）。 */
const SLUG_PATTERN = /^[a-zA-Z0-9_-]+$/;

/**
 * 与えられたパスセグメントが公開プロフィール slug として有効か判定する。
 *
 * - 予約語に一致する場合は false
 * - slug 形式（英数・ハイフン・アンダースコア、2〜50 文字）に合わない場合は false
 *   （UUID 形式のプロジェクト ID は RootSegmentDispatcher が先に判定して
 *    プロジェクトルートへ振り分けるため、ここには到達しない）
 */
export const isValidProfileSlug = (segment: string | undefined): boolean => {
  if (!segment) return false;
  if (RESERVED_SLUGS.has(segment.toLowerCase())) return false;
  if (segment.length < 2 || segment.length > 50) return false;
  return SLUG_PATTERN.test(segment);
};
